/**
 * Unit Position Utilities
 *
 * Functions for calculating unit positions in world coordinates.
 * Used when placing aircraft at unit center via dropdown selection.
 *
 * See docs/009-3d-rendering/05-unit-center-calculation.md for full explanation.
 */

import type { UnitTableRow } from '@/types/units'
import type { Building3DConfig } from '@/services/3d'

/**
 * Position in feet relative to scene center
 */
export interface UnitPosition {
  x: number // feet
  y: number // feet (always 0 for ground level)
  z: number // feet
}

/**
 * Minimal building data needed for position calculation
 */
export type BuildingPositionData = Pick<Building3DConfig, 'position' | 'rotation' | 'width' | 'depth'>

/**
 * Minimal unit data needed for position calculation
 */
export type UnitPositionData = Pick<UnitTableRow, 'offset_x' | 'offset_y' | 'interior_width'>

/**
 * Calculate the world position of a unit's center point.
 *
 * Follows the transformation pipeline from docs/009-3d-rendering/05-unit-center-calculation.md:
 *
 * 1. Calculate unit center in local space:
 *    - offset_x is the LEFT EDGE, add half width to get center X
 *    - offset_y maps to -Z (negated because +Y is north, -Z is north in 3D)
 *
 * 2. Center around building origin:
 *    - X: shift left by half building width
 *    - Z: shift forward by half building depth
 *
 * 3. Apply building rotation (Three.js Y-axis rotation matrix):
 *    - rotatedX = centeredX * cos(θ) + centeredZ * sin(θ)
 *    - rotatedZ = -centeredX * sin(θ) + centeredZ * cos(θ)
 *
 * 4. Add building world position
 *
 * @param unit - Unit data with offset and dimensions
 * @param building - Building data with position, rotation, width, and depth
 * @param centerZ - Whether to apply Z centering (+depth/2). Only true for double_row buildings.
 *   Single-row and single-unit orchestrators do NOT center Z, so this must be false for them.
 * @returns Position in feet relative to scene center
 */
export function calculateUnitCenterPosition(
  unit: UnitPositionData,
  building: BuildingPositionData,
  centerZ = false
): UnitPosition {
  // Step 1: Calculate unit center in local space
  // offset_x is the LEFT EDGE of the unit, add half width to get center X
  const unitLocalX = (unit.offset_x ?? 0) + (unit.interior_width ?? 0) / 2
  // offset_y maps to -Z (DB +Y = north, scene -Z = north since +Z = south)
  const unitLocalZ = -(unit.offset_y ?? 0)

  // Step 2: Center around building origin
  const buildingWidth = building.width ?? 0
  const buildingDepth = building.depth ?? 0
  // X: shift left by half building width
  const centeredX = unitLocalX - buildingWidth / 2
  // Z: shift forward by half building depth (only for double_row where the orchestrator centers Z)
  const centeredZ = unitLocalZ + (centerZ ? buildingDepth / 2 : 0)

  // Step 3: Apply building rotation (Three.js Y-axis rotation matrix)
  const rotationRad = ((building.rotation ?? 0) * Math.PI) / 180
  // Three.js Y-axis rotation matrix (NOT standard 2D rotation!)
  const rotatedX = centeredX * Math.cos(rotationRad) + centeredZ * Math.sin(rotationRad)
  const rotatedZ = -centeredX * Math.sin(rotationRad) + centeredZ * Math.cos(rotationRad)

  // Step 4: Add building world position
  return {
    x: building.position[0] + rotatedX,
    y: 0, // Ground level
    z: building.position[2] + rotatedZ,
  }
}
